import { useEffect, useRef } from 'react'
import type { KeyboardEvent, ReactNode } from 'react'
import type { Money } from '../../api/client'
import type { IdeenGruppe, IdeenZeile } from '../../hooks/ideenRegeln'
import { chanceRisikoAus } from '../../hooks/ideenRegeln'
import { Refs, instrumentRef } from '../common/Refs'
import { Kursleiter } from './Kursleiter'
import s from './IdeenTafel.module.scss'

/**
 * Die Handelsideen als mehrzeilige Tafel.
 *
 * Zeile 1: Instrument, der Vorschlag mit Einstufung und Status, rechts der
 * Abstand zum Einstieg. Zeile 2: die Kursleiter. Ein Klick klappt die Idee
 * auf — was darin steht, liefert `detail`. Die Gruppen lassen sich
 * zuklappen, alle Gruppen und alle Ideen auf einmal. ↑↓ wählen, Enter
 * klappt: Das hält die Seite, die Tafel zeigt nur die Wahl.
 *
 * Rechts neben dem Abstand zwei kleine Zeichen, ohne die Zeile zu klappen:
 * das Häkchen nimmt einen Vorschlag an, das Stoppzeichen lehnt ab — und
 * das Ablehnen fragt IMMER nach der Begründung, in einem Dialog der Seite.
 * Sie erscheinen nur, wenn die Seite die Rückrufe gibt, also mit Schreibrecht.
 */
export function IdeenTafel({ gruppen, offen, aktiv, zu, onToggle, onToggleGruppe,
                             onAlleGruppen, onAlleIdeen, onAnnehmen, onAblehnen,
                             detail, ziele, umrechnung, leer, fuss,
                             fussRechts = 'Klick auf eine Idee klappt Details auf · ↑↓ wählen · Enter klappt' }: {
  gruppen: IdeenGruppe[]
  /** Die aufgeklappten Ideen. */
  offen: ReadonlySet<number>
  /** Die mit der Tastatur gewählte Idee — die Kante links. */
  aktiv: number | null
  /** Die zugeklappten Gruppen. */
  zu: ReadonlySet<string>
  onToggle: (id: number) => void
  onToggleGruppe: (key: string) => void
  onAlleGruppen: (auf: boolean) => void
  onAlleIdeen: (auf: boolean) => void
  /** Das Häkchen an der Zeile — nur bei Vorschlägen. Fehlt es, fehlt das Zeichen. */
  onAnnehmen?: (zeile: IdeenZeile) => void
  /** Das Stoppzeichen an der Zeile — bei Vorschlägen und angenommenen Ideen ohne Stücke. */
  onAblehnen?: (zeile: IdeenZeile) => void
  /** Der Inhalt einer aufgeklappten Idee. */
  detail: (zeile: IdeenZeile) => ReactNode
  /** Die Kursziele einer Idee für die Leiter, sobald das Detail da ist. */
  ziele?: (id: number) => Money[] | undefined
  /** Die Zielwährung aus der Kopfzeile — die Leitern rechnen ihre Fahnen um. */
  umrechnung?: string
  leer: string
  /** Links unten, etwa „8 Ideen · Kurse vom 04.09. bis 07.09. · in Originalwährung". */
  fuss?: string
  fussRechts?: string
}) {
  const alleIdeen = gruppen.reduce((n, g) => n + g.zeilen.length, 0)
  const wurzel = useRef<HTMLDivElement>(null)

  // Die gewaehlte Zeile bekommt den Fokus: So klappt Enter genau sie, und
  // sie scrollt ins Bild — beides, was ↑↓ meinen.
  useEffect(() => {
    if (aktiv === null) return
    const ziel = wurzel.current?.querySelector<HTMLElement>(`[data-id="${aktiv}"] > [role="button"]`)
    if (ziel && document.activeElement !== ziel) ziel.focus()
  }, [aktiv])

  const offeneGruppen = gruppen.filter((g) => !zu.has(g.key)).length
  const offeneIdeen = gruppen.reduce((n, g) => n + g.zeilen.filter((z) => offen.has(z.id)).length, 0)

  return (
    <div className={s.tafel} ref={wurzel}>
      <div className={s.werkzeuge}>
        <span>{offeneGruppen} von {gruppen.length} Gruppen offen · {offeneIdeen} von {alleIdeen} Ideen aufgeklappt</span>
        <span className={s.klapp}>
          <span>Gruppen
            <button type="button" className={s.link} onClick={() => onAlleGruppen(true)}>alle auf</button>
            <span className={s.trenn}>|</span>
            <button type="button" className={s.link} onClick={() => onAlleGruppen(false)}>alle zu</button>
          </span>
          <span>Ideen
            <button type="button" className={s.link} onClick={() => onAlleIdeen(true)}>alle auf</button>
            <span className={s.trenn}>|</span>
            <button type="button" className={s.link} onClick={() => onAlleIdeen(false)}>alle zu</button>
          </span>
        </span>
      </div>
      <div className={s.spalten} aria-hidden="true">
        <span>Instrument</span><span>Idee · Einordnung</span><span>Abstand</span>
      </div>

      {alleIdeen === 0 && <div className={s.leer}>{leer}</div>}

      {gruppen.map((g) => {
        const auf = !zu.has(g.key)
        return (
          <section key={g.key}>
            <button type="button" className={s.gruppe} aria-expanded={auf}
                    onClick={() => onToggleGruppe(g.key)}>
              <span className={s.pfeil} aria-hidden="true">{auf ? '▾' : '▸'}</span>
              <span>{g.label}</span>
              <span className={s.gruppeSub}>{g.sub}</span>
              <span className={s.gruppeAnzahl}>{g.zeilen.length}</span>
            </button>
            {auf && g.zeilen.map((z, i) => (
              <Zeile key={z.key} zeile={z} offen={offen.has(z.id)} aktiv={aktiv === z.id}
                     zebra={i % 2 === 1} umrechnung={umrechnung}
                     // Alle Ziele, sobald das Detail da ist — bis dahin das
                     // erste aus der Liste, damit Leiter und Chance stehen.
                     ziele={ziele?.(z.id) ?? (z.idee.first_target ? [z.idee.first_target] : undefined)}
                     onToggle={() => onToggle(z.id)}
                     onAnnehmen={onAnnehmen} onAblehnen={onAblehnen}>
                {offen.has(z.id) && detail(z)}
              </Zeile>
            ))}
          </section>
        )
      })}

      {fuss && (
        <div className={s.fuss}>
          <span>{fuss}</span>
          <span>{fussRechts}</span>
        </div>
      )}
    </div>
  )
}

function Zeile({ zeile: z, offen, aktiv, zebra, ziele, umrechnung, onToggle,
                 onAnnehmen, onAblehnen, children }: {
  zeile: IdeenZeile
  offen: boolean
  aktiv: boolean
  zebra: boolean
  ziele?: Money[]
  umrechnung?: string
  onToggle: () => void
  onAnnehmen?: (zeile: IdeenZeile) => void
  onAblehnen?: (zeile: IdeenZeile) => void
  children?: ReactNode
}) {
  // Wer noch anzunehmen ist, und wer sich ablehnen laesst: ein Vorschlag
  // oder eine angenommene Idee — solange keine Stuecke da sind. Was im
  // Markt ist, wird verkauft und abgeschlossen, nicht abgelehnt.
  const status = z.idee.status
  const annehmbar = onAnnehmen !== undefined && status === 'PROPOSED'
  const ablehnbar = onAblehnen !== undefined && !z.idee.position.open
    && (status === 'PROPOSED' || status === 'ACTIVE')
  // Chance und Risiko in Prozent, vom Schnitt bis zum ersten Ziel und bis
  // zur Marke. Das Ziel kommt mit dem Detail — bis dahin steht ein Strich.
  const cr = chanceRisikoAus(z.idee, ziele)
  function onKey(event: KeyboardEvent<HTMLDivElement>) {
    // Nur, wenn die Zeile selbst den Fokus hat — nicht der Verweis am
    // Namen darin: Enter auf ihm klappt sein Popover, nicht die Zeile.
    if (event.target !== event.currentTarget) return
    if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); onToggle() }
  }
  return (
    <div className={s.idee} data-id={z.id} data-aktiv={aktiv || undefined}
         data-offen={offen || undefined} data-beendet={z.beendet || undefined}>
      <div role="button" tabIndex={0} className={s.zeile} data-zebra={zebra && !offen ? '' : undefined}
           aria-expanded={offen} onClick={onToggle} onKeyDown={onKey}>
        <div className={s.raster}>
          <div className={s.instrument}>
            {/*
              DERSELBE VERWEIS WIE IN DEN LISTEN: Ein Klick auf den Namen
              zeigt die Eckdaten — ISIN mit Kopierknopf, Börse, Kapitalisierung,
              Kurs, Ampel, Meldungen — und den Sprung auf die Stammdaten,
              ohne die Zeile zu klappen.
            */}
            <div className={s.name}>
              <Refs refs={[instrumentRef(z.idee.isin, z.name, z.idee.instrument_id)]} leer="—" />
            </div>
            <div className={s.ort} title={z.ort}>{z.ort}</div>
          </div>
          <div className={s.mitte}>
            <div className={s.headline}>{z.headline}</div>
            <div className={s.marken}>
              <span className={s.klasse}>{z.klasse}</span>
              <span className={s.status} data-ton={z.statusTon}>{z.status}</span>
              <span className={s.meta}>{z.meta}</span>
            </div>
          </div>
          <div className={s.abstand}>
            <div className={s.crv} title={`Chance ${cr.chanceSub || '—'} · Risiko ${cr.risikoSub || '—'} · Score ${cr.score}`}>
              <div className={s.crvZeile}>
                <span className={s.crvChance}>{cr.chanceKurz}</span>
                <span className={s.crvSlash}>/</span>
                <span className={s.crvRisiko}>{cr.risikoKurz}</span>
              </div>
              <div className={s.crvScore}>C/R-Score <b>{cr.score}</b></div>
            </div>
            <div className={s.abstandText}>
              <div className={s.abstandWert} data-ton={z.abstandTon}>{z.abstand}</div>
              <div className={s.abstandSub}>{z.abstandSub}</div>
            </div>
            {/*
              Die Zeichen halten den Klick bei sich: Er soll die Idee annehmen
              oder ablehnen, nicht die Zeile klappen.
            */}
            {(annehmbar || ablehnbar) && (
              <div className={s.aktionen}>
                {annehmbar && (
                  <button type="button" className={s.aktion} data-art="annehmen"
                          title="Annehmen" aria-label="Annehmen"
                          onClick={(event) => { event.stopPropagation(); onAnnehmen(z) }}>
                    ✓
                  </button>
                )}
                {ablehnbar && (
                  <button type="button" className={s.aktion} data-art="ablehnen"
                          title="Ablehnen — mit Begründung" aria-label="Ablehnen"
                          onClick={(event) => { event.stopPropagation(); onAblehnen(z) }}>
                    <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true">
                      <polygon points="3.6,0.5 8.4,0.5 11.5,3.6 11.5,8.4 8.4,11.5 3.6,11.5 0.5,8.4 0.5,3.6"
                               fill="none" stroke="currentColor" strokeWidth="1.4" />
                      <rect x="3.4" y="5.3" width="5.2" height="1.4" fill="currentColor" />
                    </svg>
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
        <div className={s.leiter}>
          <Kursleiter idee={z.idee} ziele={ziele} gross={offen} umrechnung={umrechnung} />
        </div>
      </div>
      {offen && <div className={s.ausklapp}>{children}</div>}
    </div>
  )
}
